/**
 * Shared access pass tier metadata (pricing UI only).
 * Single source of truth for Plans page and Settings.
 *
 * Tiers differentiate on value (skills covered, AI feedback, level/provider
 * breadth), not just access duration.
 *
 * @module accessPasses
 */

export interface AccessPassFeature {
  readonly key: string;
  readonly default: string;
}

export interface AccessPassTier {
  readonly id: string;
  readonly durationLabelKey: string;
  readonly durationDefault: string;
  readonly price: string;
  readonly positioningKey: string;
  readonly positioningDefault: string;
  readonly summaryKey: string;
  readonly summaryDefault: string;
  readonly framingKey: string;
  readonly framingDefault: string;
  readonly features: readonly AccessPassFeature[];
  readonly highlight: boolean;
}

export const ACCESS_PASSES: readonly AccessPassTier[] = [
  {
    id: 'starter',
    durationLabelKey: 'plans.tierStarter.title',
    durationDefault: 'Starter Pass',
    price: '€9.99',
    positioningKey: 'plans.tierStarter.positioning',
    positioningDefault: '7-day access',
    summaryKey: 'plans.tierStarter.summary',
    summaryDefault: 'Full exam modules with AI feedback on writing and speaking.',
    framingKey: 'plans.tierStarter.framing',
    framingDefault: 'Full exam modules with AI feedback',
    features: [
      {
        key: 'plans.tierStarter.feature.mocks',
        default: '3 full exam modules at one chosen level',
      },
      {
        key: 'plans.tierStarter.feature.skills',
        default: 'All four skills — reading, listening, writing, and speaking',
      },
      {
        key: 'plans.tierStarter.feature.report',
        default: 'AI feedback on writing and speaking',
      },
    ],
    highlight: false,
  },
  {
    id: 'exam-ready',
    durationLabelKey: 'plans.tierExamReady.title',
    durationDefault: 'Exam-Ready Pass',
    price: '€19.99',
    positioningKey: 'plans.tierExamReady.positioning',
    positioningDefault: '30-day access',
    summaryKey: 'plans.tierExamReady.summary',
    summaryDefault: 'Everything you need for your exam date.',
    framingKey: 'plans.tierExamReady.framing',
    framingDefault: 'Everything you need for your exam date',
    features: [
      {
        key: 'plans.tierExamReady.feature.unlimited',
        default: 'Unlimited mock exams at one chosen level',
      },
      {
        key: 'plans.tierExamReady.feature.allSkills',
        default: 'All four skills — including AI feedback on writing and speaking',
      },
      {
        key: 'plans.tierExamReady.feature.humanReviews',
        default: '2 human expert reviews for writing and speaking feedback',
      },
      {
        key: 'plans.tierExamReady.feature.analytics',
        default: 'Detailed weak-area analytics and recommended drills',
      },
    ],
    highlight: true,
  },
  {
    id: 'full-prep',
    durationLabelKey: 'plans.tierFullPrep.title',
    durationDefault: 'Full Prep Pass',
    price: '€34.99',
    positioningKey: 'plans.tierFullPrep.positioning',
    positioningDefault: '90-day access',
    summaryKey: 'plans.tierFullPrep.summary',
    summaryDefault: 'For serious candidates and tutors.',
    framingKey: 'plans.tierFullPrep.framing',
    framingDefault: 'For serious candidates and tutors',
    features: [
      {
        key: 'plans.tierFullPrep.feature.allLevels',
        default:
          'Unlimited mocks across all levels and providers (Goethe, ÖSD, telc)',
      },
      {
        key: 'plans.tierFullPrep.feature.aiFeedback',
        default: 'AI feedback on writing and speaking',
      },
      {
        key: 'plans.tierFullPrep.feature.priorityHumanReviews',
        default: '5 priority human expert reviews for writing and speaking feedback',
      },
      {
        key: 'plans.tierFullPrep.feature.studyPlan',
        default: 'Diagnostic test and personalized study plan',
      },
      {
        key: 'plans.tierFullPrep.feature.retake',
        default:
          'Free 90-day retake window if you do not pass your real exam',
      },
    ],
    highlight: false,
  },
];
